import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map, shareReplay } from 'rxjs';
import { environment } from '../../../environments/environment';

export interface ProductPrice {
  id: string;
  amount: number;
  currency: string;
  interval: 'month' | 'year';
  intervalCount: number;
  trialAmount?: number;
  trialPeriod?: number;
}

export interface ProductMetadata {
  displayName: string;
  badge: string | null;
  badgeStyle: 'teal' | 'yellow' | null;
  secondaryBadge: string | null;
  secondaryBadgeStyle: 'teal' | 'yellow' | null;
  displayPrice: string;
  originalPrice: string | null;
  priceSubtext: string;
  isDefault: boolean;
  order: number;
}

export interface Product {
  id: string;
  name: string;
  metadata: ProductMetadata;
  price: ProductPrice;
  hasTrial: boolean;
  status: 'active' | 'inactive';
}

export interface ProductsResponse {
  products: Product[];
}

/** Merchant data for Solidgate SDK initialization */
export interface MerchantData {
  merchant: string;
  signature: string;
  paymentIntent: string;
}

export interface SubscriptionInitResponse {
  merchantData: MerchantData;
}

export interface CancelSubscriptionResponse {
  success: boolean;
  message: string;
}

interface ApiResponse<T> {
  success?: boolean;
  products?: Product[];
}

@Injectable({
  providedIn: 'root',
})
export class BillingApiService {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = `${environment.apiUrl}/billing`;

  /** Cached products observable - fetched once and shared */
  private products$: Observable<Product[]> | null = null;

  /**
   * Get available subscription products
   * Cached to avoid refetching on every component mount
   */
  getProducts(): Observable<Product[]> {
    if (!this.products$) {
      this.products$ = this.http
        .get<ProductsResponse>(`${this.apiUrl}/products`)
        .pipe(
          map((res) => res.products.sort((a, b) => a.metadata.order - b.metadata.order)),
          shareReplay(1)
        );
    }
    return this.products$;
  }

  /**
   * Clear cached products (useful after subscription changes)
   */
  clearCache(): void {
    this.products$ = null;
  }

  /**
   * Initialize a subscription for the given product
   * Returns merchant data for Solidgate SDK
   *
   * @param productId - The product ID to subscribe to
   * @returns Observable with merchant data for payment form initialization
   */
  initializeSubscription(productId: string): Observable<MerchantData> {
    return this.http
      .post<SubscriptionInitResponse>(`${this.apiUrl}/subscription/init`, { productId })
      .pipe(map((res) => res.merchantData));
  }

  /**
   * Cancel the current subscription
   * Subscription will be canceled at the end of the current billing period
   *
   * @returns Observable with success status and message
   */
  cancelSubscription(): Observable<CancelSubscriptionResponse> {
    return this.http.post<CancelSubscriptionResponse>(`${this.apiUrl}/subscription/cancel`, {});
  }
}

